import {
  MenuItem,
  TextField,
  Grid,
  Box,
  Checkbox,
  useMediaQuery,
  useTheme,
  Tooltip,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { HelpOutline } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { Button } from "components";
import { useFormik } from "formik";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { InputImage } from "shared";
import { categoriesSelect, subCategoriesSelect } from "utils";
import { mapStatesString } from "utils/mapStates";

import { StyledFormControlLabel, useStyles } from "./styles";
import {
  validationSchemaCreate,
  validationSchemaEdit
} from "./validationSchema";

const Component = (
  { initialValues, onSubmit, productCategoryData, productSubCategoryData },
  ref
) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const formik = useFormik({
    initialValues: initialValues || {
      isCertificateRequired: false
    },
    validationSchema: initialValues
      ? validationSchemaEdit
      : validationSchemaCreate,
    onSubmit: onSubmit,
    validateOnChange: false
  });

  useImperativeHandle(ref, () => ({ resetForm: formik.resetForm }));

  const filteredSubCategory = useMemo(() => {
    const selectedCategoryId = formik.values.productCategory;
    return productSubCategoryData.filter(
      ({ productCategory }) => productCategory.id === selectedCategoryId
    );
  }, [formik.values.productCategory]);

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Box display="flex" className={classes.gridImage}>
        <Grid container xs={12} sm={5} justifyContent="center">
          <InputImage formik={formik} name="base64" />
        </Grid>
        <Grid container xs={12} sm={7}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="name"
              label="Nome"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                !!formik.errors.name
              }
              helperText={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
              }
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} style={{ marginTop: "1rem" }}>
            <TextField
              select
              fullWidth
              name="purchaseTypeId"
              label="Tipo de estoque"
              value={formik.values.purchaseTypeId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.purchaseTypeId || formik.submitCount > 0) &&
                !!formik.errors.purchaseTypeId
              }
              helperText={
                (formik.touched.purchaseTypeId || formik.submitCount > 0) &&
                formik.errors.purchaseTypeId
              }
              variant="outlined"
            >
              <MenuItem value={1}>Quilo (kg)</MenuItem>
              <MenuItem value={2}>Unidade (un)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} style={{ marginTop: "1rem" }}>
            <TextField
              select
              fullWidth
              name="category"
              label="Categoria"
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.category || formik.submitCount > 0) &&
                !!formik.errors.category
              }
              helperText={
                (formik.touched.category || formik.submitCount > 0) &&
                formik.errors.category
              }
              variant="outlined"
            >
              {categoriesSelect.map(({ name, value }) => (
                <MenuItem key={value} value={value}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>
      <Grid
        container
        xs={12}
        spacing={2}
        justify="center"
        justifyContent="center"
        style={{ marginTop: "0.5rem" }}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="subCategory"
            label="Subcategoria"
            value={formik.values.subCategory}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.subCategory || formik.submitCount > 0) &&
              !!formik.errors.subCategory
            }
            helperText={
              (formik.touched.subCategory || formik.submitCount > 0) &&
              formik.errors.subCategory
            }
            variant="outlined"
          >
            {subCategoriesSelect.map(({ name, value }) => (
              <MenuItem key={value} value={value}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="status"
            label="Status"
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.status || formik.submitCount > 0) &&
              !!formik.errors.status
            }
            helperText={
              (formik.touched.status || formik.submitCount > 0) &&
              formik.errors.status
            }
            variant="outlined"
          >
            <MenuItem value={"active"}>{mapStatesString("active")}</MenuItem>
            <MenuItem value={"deactivated"}>
              {mapStatesString("deactivated")}
            </MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="productCategory"
            label="Categoria - (V2)"
            value={formik.values.productCategory}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            variant="outlined"
          >
            {productCategoryData?.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="productSubCategory"
            label="Subcategoria - (V2)"
            value={formik.values.productSubCategory}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            variant="outlined"
          >
            {filteredSubCategory?.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="isCertificateRequired"
            label="Requer certificado"
            value={formik.values.isCertificateRequired}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            variant="outlined"
            defaultValue={false}
          >
            <MenuItem value={false}>Não</MenuItem>
            <MenuItem value={true}>Sim</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="barcode"
            label="Código de barras (Opcional)"
            value={formik.values.barcode}
            onChange={(e, ...rest) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, "");
              formik.handleChange(e, ...rest);
            }}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.barcode || formik.submitCount > 0) &&
              !!formik.errors.barcode
            }
            helperText={
              (formik.touched.barcode || formik.submitCount > 0) &&
              formik.errors.barcode
            }
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="unitWeight"
            label="Peso da unidade (kg)"
            type="number"
            value={formik.values.unitWeight}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.unitWeight || formik.submitCount > 0) &&
              !!formik.errors.unitWeight
            }
            helperText={
              (formik.touched.unitWeight || formik.submitCount > 0) &&
              formik.errors.unitWeight
            }
            variant="outlined"
            inputProps={{
              step: "0.001"
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Menor porcionamento que pode ser enviado ao cliente. Ex: 1bdj Morango -> 250g,  1 laranja -> 74g. O valor alimenta vários lugares inclusive o débito de pré extra order no estoque">
                    <IconButton size="small" edge="end">
                      <HelpOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid
          item
          container
          justifyContent={isMobile ? "flex-start" : "flex-end"}
        >
          <StyledFormControlLabel
            control={<Checkbox name="isSanitized" />}
            label="Higienizado"
            checked={formik.values.isSanitized}
            onChange={formik.handleChange}
          />
          <StyledFormControlLabel
            control={<Checkbox name="isDiverse" />}
            label="Diverso"
            checked={formik.values.isDiverse}
            onChange={formik.handleChange}
          />
          <StyledFormControlLabel
            control={<Checkbox name="isProcessed" />}
            label="Processado"
            checked={formik.values.isProcessed}
            onChange={formik.handleChange}
          />
          <StyledFormControlLabel
            control={<Checkbox name="isGreen" />}
            label="Verde"
            checked={formik.values.isGreen}
            onChange={formik.handleChange}
          />
        </Grid>

        {!initialValues && (
          <Grid item xs={12}>
            <Alert severity="error">
              Após salvar, cadastre este produto no Omie. Sem o cadastro no
              Omie, a NF não será emitida e a operação ficará travada.
            </Alert>
          </Grid>
        )}

        <Grid item container justifyContent="flex-end">
          <Button
            isLoading={formik.isSubmitting}
            variant="contained"
            type="submit"
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export const Form = forwardRef(Component);
